<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import TruckStatus from "@/components/statuses/TruckStatus.vue";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import TruckMember from "@/components/truck/TruckMember.vue";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { TravelOrderDto, TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import type { TruckDriverDto, TruckDto, TruckOwnerDto } from "@/api/trucks/dto/truck.dto";
import type { VForm } from "vuetify/components";
import {
  RouteStopStatus,
  TravelOrderCancelStatuses,
  TravelOrderEditStatusMap,
  TravelOrderStatus,
} from "@/constants/trip";
import { OrderStatus } from "@/constants/order";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import TravelOrderSection from "@/views/trips/edit/components/TravelOrderSection.vue";
import TripOrderSection from "@/views/trips/edit/components/TripOrderSection.vue";
import type { TravelOrderPaymentType } from "@/constants/travelOrder";
import { useOrderPayments, useTrip } from "@/composables";
import type { TripOrder, TripRoute, TripRouteStop } from "@/types/trip";
import type { OrderPayment } from "@/api/orders/dto/order.dto";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { OwnerDto } from "@/api/users/dto/owner.dto";
import UndoFinishDialog from "@/views/trips/view/components/dialogs/UndoFinishDialog.vue";
import { useError } from "@/composables/useError";
import { isSameEntity } from "@/utils/sameEntity";

const { t } = useI18n();
const { createMandatoryOrderPayment, createMandatoryTravelOrderPayment } = useOrderPayments();
const { getRouteDriver, getRouteOwner } = useTrip();
const { scrollToError } = useError();

const isEditDialog = defineModel<boolean>({ default: false });
const travelOrder = defineModel<TravelOrderDto | null>("travelOrder", { default: null });

const props = defineProps<{
  route: TripRoute;
  truck: TruckDto;
  travelOrderFromApi: TravelOrderDto | null;
  routeStops: TripRouteStop[];
  travelOrdersByOrder: Record<string, TravelOrderDto[]>;
  order: TripOrder | null;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:travelOrder", travelOrder: TravelOrderDto): void;
  (e: "updateOrderPayments", orderId: string, payments: OrderPayment[]): void;
}>();

const api = new API();
const { showSnackbarError } = useSnackbarStore();

const loading = ref<boolean>(false);
const travelOrderStatusModel = ref<TravelOrderStatus>(travelOrder.value?.status || TravelOrderStatus.active);
const orderStatusModel = ref<OrderStatus>(travelOrder.value?.order.status || OrderStatus.inProgress);
const tonuFile = ref<File | null>(null);
const isOrderDetail = ref<boolean>(false);
const editFormRef = useTemplateRef<VForm | null>("editForm");
const travelOrderPayments = ref<TravelOrderPayment[]>([...(travelOrder.value?.payments || [])]);
const orderPayments = ref<OrderPayment[]>(props.order?.orderPayments || []);
const driver = ref<TruckDriverDto | DriverDto | null>(null);
const owner = ref<TruckOwnerDto | OwnerDto | null>(null);
const showUndoFinishDialog = ref(false);

const isOrderCompleted = computed<boolean>(() => {
  return props.travelOrderFromApi?.order?.status === OrderStatus.completed;
});

const isTravelOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [RouteStopStatus.inRoute, RouteStopStatus.waitingForLoading, RouteStopStatus.notStarted];

  return props.routeStops.some((routeStop) => !acceptedStatuses.includes(routeStop.status));
});

const travelOrderStatusOptions = computed(() => {
  const dryRunStatuses = [TravelOrderStatus.dryRun, TravelOrderStatus.waitingForDryRun, TravelOrderStatus.noDryRun];

  if (props.travelOrderFromApi && dryRunStatuses.includes(props.travelOrderFromApi.status)) {
    return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.cancelled);
  }

  return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.noDryRun);
});

const travelOrdersByOrder = computed<TravelOrderDto[]>(() => {
  return props.travelOrdersByOrder[travelOrder.value?.orderId || ""].map((to) => {
    if (travelOrder.value && isSameEntity(to, travelOrder.value)) {
      return {
        ...to,
        status: travelOrderStatusModel.value,
      };
    }

    return { ...to };
  });
});

const hasActiveTravelOrder = computed<boolean>(() => {
  return travelOrdersByOrder.value.some((travelOrder) => travelOrder.status === TravelOrderStatus.active);
});

const isOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [
    TravelOrderStatus.dryRun,
    TravelOrderStatus.waitingForDryRun,
    TravelOrderStatus.noDryRun,
    TravelOrderStatus.cancelled,
  ];

  return travelOrdersByOrder.value.some((travelOrder) => !acceptedStatuses.includes(travelOrder.status));
});

const isReturnedFromDryRun = computed<boolean>(() => {
  return (
    props.travelOrderFromApi?.status === TravelOrderStatus.dryRun &&
    travelOrderStatusModel.value === TravelOrderStatus.active
  );
});

const isUndoProcess = computed(() => {
  return (
    travelOrderStatusModel.value === TravelOrderStatus.active && travelOrder.value?.status !== TravelOrderStatus.active
  );
});

const fileLabel = computed(() => {
  if (isUndoProcess.value) {
    return "Rate confirmation";
  }

  return "TONU file";
});

function deduplicatePaymentsByType(payments: TravelOrderPayment[]): TravelOrderPayment[] {
  const map = new Map<TravelOrderPaymentType, TravelOrderPayment>();

  payments.forEach((payment) => {
    if (!payment.type) {
      return;
    }

    if (!map.has(payment.type)) {
      map.set(payment.type, payment);
    }
  });

  return Array.from(map.values());
}

function updatePaymentsByTravelOrderStatus(status: TravelOrderStatus) {
  if (!travelOrder.value) {
    return;
  }

  travelOrderPayments.value = deduplicatePaymentsByType(
    createMandatoryTravelOrderPayment(travelOrderPayments.value, status),
  );
}

async function editTravelOrder() {
  if (!editFormRef.value) {
    return;
  }

  const result = await editFormRef.value.validate();

  if (!result.valid) {
    scrollToError();
    return;
  }

  const isTOActiveStatus = !TravelOrderCancelStatuses.includes(travelOrderStatusModel.value);
  const wasCancelled = props.travelOrderFromApi && TravelOrderCancelStatuses.includes(props.travelOrderFromApi.status);

  if (isTOActiveStatus && wasCancelled) {
    showUndoFinishDialog.value = true;

    return;
  }

  await emitTravelOrder();
}

async function emitTravelOrder() {
  if (!travelOrder.value) {
    return;
  }

  loading.value = true;

  if (isOrderDetail.value && tonuFile.value) {
    const fileResponse = await api.files.uploadFile("orders", tonuFile.value, [
      `order:${travelOrder.value.orderId}`,
      OrderStatus.tonu,
    ]);

    if (!fileResponse.success) {
      showSnackbarError("Error uploading TONU file");
      loading.value = false;
      return;
    }
  }

  const updatedTravelOrder: TravelOrderDto = {
    ...travelOrder.value,
    status: travelOrderStatusModel.value,
    order: {
      ...travelOrder.value.order,
      status: orderStatusModel.value,
    },
    payments: travelOrderPayments.value.map((payment) => {
      return {
        ...payment,
        amount: Number(payment.amount),
      };
    }),
  };

  loading.value = false;

  emits("updateOrderPayments", updatedTravelOrder.orderId, orderPayments.value);
  emits("update:travelOrder", updatedTravelOrder);
  emits("update:modelValue", false);
}

async function scrollToBottom() {
  await nextTick();

  const formParent = editFormRef.value?.$parent?.$el;

  if (!formParent) {
    return;
  }

  formParent.scrollTo({
    top: formParent.scrollHeight,
    behavior: "smooth",
  });
}

watch(isOrderStatusDisabled, (value, prevValue) => {
  if (!value && prevValue && !isOrderCompleted.value) {
    orderStatusModel.value = OrderStatus.unassigned;
  }
});

watch(travelOrderStatusModel, (value) => {
  updatePaymentsByTravelOrderStatus(value);
});

watch(orderStatusModel, (value) => {
  orderPayments.value = createMandatoryOrderPayment(orderPayments.value, value);
});

watch(
  hasActiveTravelOrder,
  (value) => {
    if (value && !isOrderCompleted.value) {
      orderStatusModel.value = OrderStatus.inProgress;
    }
  },
  { immediate: true },
);

watch(
  orderStatusModel,
  (value) => {
    if (value === OrderStatus.tonu) {
      isOrderDetail.value = true;
    } else if (value === OrderStatus.noTonu || value === OrderStatus.cancelled) {
      isOrderDetail.value = false;
    } else {
      isOrderDetail.value = false;
    }
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  driver.value = await getRouteDriver(props.route.driverId, props.truck);
  owner.value = await getRouteOwner(props.route.ownerId, props.truck);
});
</script>

<template>
  <div>
    <omni-dialog
      v-if="travelOrder"
      v-model="isEditDialog"
      :loading="loading"
      :title="`Edit ${travelOrder.name}`"
      :width="800"
      primary-action-label="Save"
      @primary-action:click="editTravelOrder"
    >
      <v-form ref="editForm" class="d-flex flex-column row-gap-6 py-4">
        <div v-if="truck" class="d-flex flex-column row-gap-2 pa-4 bg-grey-lighten-4 rounded-lg">
          <div class="d-flex align-center col-gap-3">
            <router-link
              :to="{ name: 'truck-view', params: { id: truck.id } }"
              class="text-primary text-decoration-none font-weight-500"
              data-qa="edit-travel-order-truck-number"
              target="_blank"
            >
              Truck {{ truck.number }}
            </router-link>

            <truck-status :truck="truck" />
          </div>

          <div class="d-flex align-start edit-travel-order__truck-info">
            <div class="d-flex flex-column row-gap-2">
              <span class="edit-travel-order__caption">{{ t("trucks.current-location") }}</span>

              <truck-location-info :location="truck.currentLocation" :time="truck.currentLocationTime" />
            </div>

            <div v-if="driver || owner" class="d-flex flex-column row-gap-2">
              <span class="edit-travel-order__caption">{{ t("general.contacts") }}:</span>

              <div class="d-flex flex-column row-gap-3">
                <truck-member
                  v-if="driver"
                  :member="driver"
                  class="d-flex align-center"
                  hide-status
                  label-class="bg-shades-white"
                  language-light
                />

                <truck-member
                  v-if="owner"
                  :member="owner"
                  class="d-flex align-center"
                  hide-status
                  label-class="bg-shades-white text-green-lighten-1"
                  language-light
                />
              </div>
            </div>
          </div>
        </div>

        <travel-order-section
          v-model:payments="travelOrderPayments"
          v-model:status="travelOrderStatusModel"
          :is-status-disabled="isTravelOrderStatusDisabled"
          :status-from-api="props.travelOrderFromApi?.status"
          :status-options="travelOrderStatusOptions"
          @scroll-bottom="scrollToBottom"
        />

        <trip-order-section
          v-model:file="tonuFile"
          v-model:payments="orderPayments"
          v-model:status="orderStatusModel"
          :file-label="fileLabel"
          :is-file-visible="isOrderDetail"
          :is-returned-from-dry-run="isReturnedFromDryRun"
          :is-status-disabled="isOrderStatusDisabled"
          :order-id="travelOrder.orderId"
          :order-number="travelOrder.order.number"
          :status-from-api="props.travelOrderFromApi?.order?.status"
        />
      </v-form>
    </omni-dialog>

    <undo-finish-dialog
      v-if="showUndoFinishDialog"
      v-model="showUndoFinishDialog"
      :route="props.route"
      :truck="props.truck"
      title="Undo finish"
      @undo="emitTravelOrder"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.edit-travel-order {
  &__truck-info {
    column-gap: 64px;
  }

  &__caption {
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
    color: $grey;
  }
}
</style>
